import { TXN_PANEL_DIALOG_CLASS } from "@app/components/txn-table-panel";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type {
  qboPnlCellPanel_Query,
  QboPnlLineDrillEnum,
} from "@app/queries/qboPnlCellPanel_Query.graphql";

// What a clicked QBO-sourced P&L cell drills into: one line, one fiscal year.
export type QboPnlCellScope = {
  line: QboPnlLineDrillEnum;
  year: number;
  label: string;
  // The number printed in the cell, so the panel can say out loud when the list
  // it shows is a different set than the cell (Other OpEx, see NOTES).
  cellValue: number | null | undefined;
};

// Other OpEx is QuickBooks' Expenses section MINUS the Marketing (S&M)
// reclassification, which lives on its own row above. The detail here is the
// whole section, so the two totals differ by exactly that reclassification —
// said plainly rather than quietly mismatched.
const NOTES: Partial<Record<QboPnlLineDrillEnum, string>> = {
  OTHER_OPEX:
    "This lists QuickBooks' whole Expenses section. The cell above is that minus the Marketing (S&M) reclassification, which has its own row.",
};

const PAGE_SIZE = 500;

const cellQuery = graphql`
  query qboPnlCellPanel_Query(
    $line: QboPnlLineDrillEnum!
    $fiscalYear: Int!
    $limit: Int
    $offset: Int
    $sort: String
    $sortDirection: String
  ) {
    qboPnlCell(
      line: $line
      fiscalYear: $fiscalYear
      limit: $limit
      offset: $offset
      sort: $sort
      sortDirection: $sortDirection
    ) {
      staged
      totalCount
      totalAmount
      limit
      rows {
        id
        txnDate
        entityType
        docNum
        counterparty
        memo
        splitAccount
        accountName
        groupKey
        amount
      }
    }
  }
`;

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

const fmt = (value: number) => usd.format(value);

type SortColumn = "DATE" | "AMOUNT" | "COUNTERPARTY" | "ACCOUNT";

const COLUMNS: ReadonlyArray<{
  key: string;
  label: string;
  sort?: SortColumn;
  align?: "right";
}> = [
  { key: "date", label: "Date", sort: "DATE" },
  { key: "type", label: "Type" },
  { key: "num", label: "Num" },
  { key: "name", label: "Name", sort: "COUNTERPARTY" },
  { key: "memo", label: "Memo" },
  { key: "split", label: "Split" },
  { key: "account", label: "Account", sort: "ACCOUNT" },
  { key: "amount", label: "Amount", sort: "AMOUNT", align: "right" },
];

export default function QboPnlCellPanel({
  scope,
  onClose,
}: {
  scope: QboPnlCellScope;
  onClose: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20" />
        <Dialog.Content className={TXN_PANEL_DIALOG_CLASS} aria-describedby={undefined}>
          <Dialog.Title className="sr-only">{scope.label}</Dialog.Title>
          <Suspense
            fallback={
              <div className="p-6 text-sm text-gray-400">
                Asking QuickBooks for the transactions behind this number…
              </div>
            }
          >
            <PanelContent scope={scope} onClose={onClose} />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PanelContent({ scope, onClose }: { scope: QboPnlCellScope; onClose: () => void }) {
  const [sort, setSort] = useState<SortColumn>("DATE");
  const [direction, setDirection] = useState<"ASC" | "DESC">("ASC");
  const [page, setPage] = useState(0);

  const data = useLazyLoadQuery<qboPnlCellPanel_Query>(
    cellQuery,
    {
      line: scope.line,
      fiscalYear: scope.year,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      sort,
      sortDirection: direction,
    },
    { fetchPolicy: "store-or-network" }
  );

  const cell = data.qboPnlCell;
  const rows = cell.rows;
  const pageCount = Math.max(1, Math.ceil(cell.totalCount / cell.limit));
  const note = NOTES[scope.line];

  const onSort = (column: SortColumn) => {
    setPage(0);
    if (column === sort) {
      setDirection((prev) => (prev === "ASC" ? "DESC" : "ASC"));
      return;
    }
    setSort(column);
    setDirection(column === "AMOUNT" ? "DESC" : "ASC");
  };

  return (
    <>
      <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{scope.label}</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {cell.totalCount.toLocaleString()} transaction
            {cell.totalCount === 1 ? "" : "s"} · {fmt(cell.totalAmount)}
            {scope.cellValue != null && (
              <span className="text-gray-400"> · the cell reads {fmt(scope.cellValue)}</span>
            )}
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            Straight from QuickBooks&apos; own Profit &amp; Loss Detail report — the same books the
            number above came from.
          </p>
          {note && <p className="mt-1 text-xs text-amber-700">{note}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded px-2 py-1 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            {cell.staged
              ? `QuickBooks reports no transactions in ${scope.label}.`
              : "QuickBooks' detail for this year could not be loaded. Run a QuickBooks sync from Sources and it will fill in."}
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 ${
                      column.align === "right" ? "text-right" : "text-left"
                    }`}
                  >
                    {column.sort ? (
                      <button
                        type="button"
                        onClick={() => onSort(column.sort as SortColumn)}
                        className="uppercase hover:text-gray-900"
                      >
                        {column.label}
                        {sort === column.sort && (direction === "ASC" ? " ↑" : " ↓")}
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-1.5 text-gray-600">{row.txnDate ?? "—"}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">{row.entityType ?? "—"}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">{row.docNum ?? ""}</td>
                  <td className="px-3 py-1.5 text-gray-800">{row.counterparty ?? "—"}</td>
                  <td className="max-w-[22rem] truncate px-3 py-1.5 text-gray-500" title={row.memo ?? ""}>
                    {row.memo ?? ""}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">{row.splitAccount ?? ""}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-gray-600">{row.accountName ?? "—"}</td>
                  <td
                    className={`whitespace-nowrap px-3 py-1.5 text-right font-mono tabular-nums ${
                      row.amount < 0 ? "text-red-600" : "text-gray-900"
                    }`}
                  >
                    {fmt(row.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-5 py-3 text-xs text-gray-500">
          <span>
            Rows {page * cell.limit + 1}–{Math.min((page + 1) * cell.limit, cell.totalCount)} of{" "}
            {cell.totalCount.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded border px-2 py-1 hover:bg-gray-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border px-2 py-1 hover:bg-gray-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
}
